import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Spinner, Alert } from "react-bootstrap";
import BaseUrl from '../../ApiServices/BaseUrl';
import Navbar from '../../Pages/Navbar/Navbar';
import EmployerNavbar from '../../Pages/Navbar/EmployerNavbar';
import JobSeekerNavbar from '../../Pages/Navbar/JobSeekerNavbar';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import Footer from "../../Footer/Footer";
import GulfWorkerLeadBot from '../../LeadBot/GulfWorkerLeadBot';
import AgencyCard from '../../OtherPages/AgencyCard/AgencyCard';

function AgencyList() {
    const PAGE_SIZE = 15;
    const [agencies, setAgencies] = useState([]);
    const [filteredAgencies, setFilteredAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const autoSearchTimeoutRef = useRef(null);
    const isAutoSearchingRef = useRef(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [visibleCount, setVisibleCount] = useState(9);

    const user = JSON.parse(localStorage.getItem('user'));

    const renderNavbar = () => {
        if (!user) return <Navbar />;
        if (user.role === 'employer') return <EmployerNavbar />;
        if (user.role === 'job seeker') return <JobSeekerNavbar />;
        if (user.role === 'admin') return <AdminNavbar />;
        return <AgencyNavbar />;
    };

    const fetchAgenciesPage = async (pageToLoad, append = false) => {
        try {
            if (append) setLoadingMore(true);
            else {
                setLoading(true);
                setError(null);
            }

            const response = await fetch(`${BaseUrl}/api/agency?paginated=1&verified=1&page=${pageToLoad}&limit=${PAGE_SIZE}`);
            if (!response.ok) {
                throw new Error('Failed to fetch agencies');
            }

            const result = await response.json();
            const data = Array.isArray(result?.data) ? result.data : [];

            setAgencies(prev => append ? [...prev, ...data] : data);
            setFilteredAgencies(prev => append ? [...prev, ...data] : data);
            setPage(pageToLoad);
            setHasMore(Boolean(result?.hasMore));
            return { data, hasMore: Boolean(result?.hasMore) };
        } catch (err) {
            console.error("Error fetching agencies:", err);
            setError(err.message);
            return { data: [], hasMore: false };
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        const initialFetch = async () => {
            try {
                let { data: currentData, hasMore: currentHasMore } = await fetchAgenciesPage(1, false);
                let currentPage = 1;

                while (currentData.length < 9 && currentHasMore) {
                    currentPage++;
                    const result = await fetchAgenciesPage(currentPage, true);
                    currentData = [...currentData, ...result.data];
                    currentHasMore = result.hasMore;
                }
            } catch (err) {
                console.error("Initial fetch failed:", err);
            }
        };
        initialFetch();
    }, []);

    useEffect(() => {
        if (!searchTerm) {
            setFilteredAgencies(agencies);
            return;
        }
        const term = searchTerm.toLowerCase();
        const filtered = agencies.filter(agency => {
            const name = agency.agency_name?.toLowerCase() || '';
            const city = agency.city?.toLowerCase() || '';
            const country = agency.country?.toLowerCase() || '';
            const email = agency.email?.toLowerCase() || '';
            const owner = agency.owner_name?.toLowerCase() || '';
            const manager = agency.manager_name?.toLowerCase() || '';
            return (
                name.includes(term) ||
                city.includes(term) ||
                country.includes(term) ||
                email.includes(term) ||
                owner.includes(term) ||
                manager.includes(term)
            );
        });
        setFilteredAgencies(filtered);
    }, [agencies, searchTerm]);

    // Reset visible count only when user changes filters
    useEffect(() => {
        setVisibleCount(9);
    }, [searchTerm]);

    const applySearchTerm = async (rawSearchValue) => {
        setSearchTerm(rawSearchValue.trim().toLowerCase());
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        await applySearchTerm(searchInput);
    };

    const handleLoadMore = async () => {
        const nextVisible = visibleCount + 9;
        setVisibleCount(nextVisible);

        if (nextVisible >= filteredAgencies.length && hasMore && !loadingMore) {
            try {
                setLoadingMore(true);
                let currentAgencies = [...agencies];
                let currentHasMore = hasMore;
                let currentPage = page;

                while (currentHasMore && currentAgencies.length < nextVisible) {
                    const result = await fetchAgenciesPage(currentPage + 1, true);
                    currentHasMore = result.hasMore;
                    currentPage++;
                    currentAgencies = [...currentAgencies, ...result.data];
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingMore(false);
            }
        }
    };

    useEffect(() => {
        if (autoSearchTimeoutRef.current) {
            clearTimeout(autoSearchTimeoutRef.current);
        }

        autoSearchTimeoutRef.current = setTimeout(async () => {
            if (isAutoSearchingRef.current) return;

            const normalizedSearchInput = searchInput.trim().toLowerCase();
            if (normalizedSearchInput === searchTerm) return;

            isAutoSearchingRef.current = true;
            try {
                await applySearchTerm(searchInput);
            } finally {
                isAutoSearchingRef.current = false;
            }
        }, 350);

        return () => {
            if (autoSearchTimeoutRef.current) {
                clearTimeout(autoSearchTimeoutRef.current);
            }
        };
    }, [searchInput, searchTerm]);

    return (
        <div className="agency-list-page">
            {renderNavbar()}
            <Container fluid className="px-3 px-xl-3" style={{ minHeight: '600px', paddingTop: '100px', paddingBottom: '3rem' }}>
                {/* <h1 className="text-center fw-bold mb-5" style={{ color: '#0046B3' }}>Verified Recruitment Agencies</h1> */}

                <form className="mb-3" onSubmit={handleSearchSubmit}>
                    <div className="input-group w-100">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search agencies by name, location, or email"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{ minWidth: 0, height: '38px' }}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary px-3"
                            style={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: '78px', height: '38px' }}
                        >
                            Search
                        </button>
                    </div>
                </form>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Loading verified agencies...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger">
                        {error}. Please try again later.
                    </Alert>
                ) : filteredAgencies.length === 0 ? (
                    <div className="text-center py-5">
                        <h3>No verified agencies found at the moment.</h3>
                        <p>Please check back later.</p>
                    </div>
                ) : (
                    <>
                        <Row xs={1} md={2} lg={2} xl={3} className="g-2 g-md-3">
                            {filteredAgencies.slice(0, visibleCount).map(agency => (
                                <Col key={agency.id}>
                                    <AgencyCard agency={agency} />
                                </Col>
                            ))}
                        </Row>

                        {loadingMore && (
                            <div className="text-center mt-3">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                                {!loadingMore && (visibleCount < filteredAgencies.length || hasMore) && (
                                    <div className="text-center mt-4 mb-3">
                                        <button
                                            className="load-more-btn"
                                            onClick={handleLoadMore}
                                        >
                                            Load More
                                        </button>
                                    </div>
                                )}
                    </>
                )}
            </Container>
            <GulfWorkerLeadBot pageContext="Agency" />
            <Footer />
        </div>
    );
}

export default AgencyList;
